import Image from 'next/image'
import { cn } from '@/lib/utils'
import { PHOTO_GRADE } from '@/lib/visual'

type LandscapeFade = 'none' | 'bottom' | 'top-bottom' | 'edges' | 'card' | 'soft'
type LandscapeBlur = 'none' | 'sm' | 'md' | 'lg'

interface LandscapeProps {
    src: string
    alt?: string
    className?: string
    imageClassName?: string
    position?: string
    blur?: LandscapeBlur
    fade?: LandscapeFade
    sizes?: string
    priority?: boolean
    tint?: boolean
}

const blurClasses: Record<LandscapeBlur, string> = {
    none: '',
    sm: 'blur-[5px] scale-[1.06]',
    md: 'blur-[18px] scale-110',
    lg: 'blur-[34px] scale-125'
}

const fadeClasses: Record<LandscapeFade, string | null> = {
    none: null,
    bottom: 'bg-[linear-gradient(180deg,rgba(0,0,0,0.35)_0%,rgba(0,0,0,0)_24%,rgba(0,0,0,0)_50%,rgba(0,0,0,0.82)_80%,#000_100%)]',
    'top-bottom': 'bg-[linear-gradient(180deg,#000_0%,rgba(0,0,0,0.15)_24%,rgba(0,0,0,0)_45%,rgba(0,0,0,0.35)_68%,#000_100%)]',
    edges: 'bg-[radial-gradient(115%_85%_at_50%_45%,rgba(0,0,0,0)_30%,rgba(0,0,0,0.72)_72%,#000_100%)]',
    card: 'bg-[linear-gradient(180deg,rgba(0,0,0,0.15)_0%,rgba(0,0,0,0)_30%,rgba(0,0,0,0.5)_70%,rgba(0,0,0,0.92)_100%)]',
    soft: 'bg-[radial-gradient(130%_100%_at_50%_30%,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.6)_70%,rgba(0,0,0,0.9)_100%)]'
}

/**
 * A landscape photograph with the site-wide warm golden-green grade and a
 * long soft fade into black. Fills its (positioned) parent.
 */
export const Landscape = ({
    src,
    alt = '',
    className,
    imageClassName,
    position = 'center',
    blur = 'none',
    fade = 'bottom',
    sizes = '100vw',
    priority = false,
    tint = true
}: LandscapeProps) => {
    return (
        <div className={cn('absolute inset-0 isolate overflow-hidden', className)} aria-hidden={alt ? undefined : true}>
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                priority={priority}
                className={cn('object-cover transition-[filter,transform,scale,translate] duration-[1200ms] ease-cinema', PHOTO_GRADE, blurClasses[blur], imageClassName)}
                style={{ objectPosition: position }}
            />

            {tint && (
                <>
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,#f6b48a_0%,#c9774f_55%,#6b3a2a_100%)] opacity-[0.3] mix-blend-color' />
                    <div className='absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_8%,#ffc98f_0%,rgba(255,201,143,0)_62%)] opacity-70 mix-blend-soft-light' />
                </>
            )}

            {fadeClasses[fade] && <div className={cn('absolute inset-0', fadeClasses[fade])} />}
        </div>
    )
}
